<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, shallowRef, watch } from 'vue';
import { renderAsync } from 'docx-preview';
import { VuePDF, usePDF } from '@tato30/vue-pdf';
import { IconAdd, IconRemove } from '@/components/design/icons';
import { logger } from '@/utils/logger';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import { gaiaNeutral, gaiaShadow } from '@/components/design/themes/tokens';

/**
 * Shows the backend-rendered "filled with sample data" preview.
 *
 * Preferred path: the backend streams a PDF (converted via LibreOffice), and
 * pdf.js renders it page by page — page breaks are EXACT because a real
 * layout engine paginated the document. Fallback path: a .docx response is
 * rendered with docx-preview (same library as the wizard's live preview),
 * whose pagination is approximate — it only honors explicit break markers.
 *
 * Quality note: the PDF path renders each page at an explicit pixel WIDTH
 * (pdf.js then rasterizes at that width × devicePixelRatio). Zooming changes
 * the width so the page re-renders at full resolution — never CSS-scaling a
 * fixed bitmap, which would blur. The docx fallback is vector HTML text, so
 * it can use a plain CSS `zoom` and stay crisp.
 */

const props = defineProps<{
  show: boolean;
  blob: Blob | null;
}>();
const emit = defineEmits<{ (e: 'update:show', value: boolean): void }>();

const showModal = computed({
  get: () => props.show,
  set: value => {
    emit('update:show', value);
  },
});

const scrollHost = ref<HTMLElement | null>(null);
const previewContainer = ref<HTMLElement | null>(null);
const rendering = ref<boolean>(false);
const renderError = ref<string | null>(null);

const previewMode = ref<'pdf' | 'docx' | null>(null);
/**
 * The pdf.js loading task, kept in a SHALLOW ref on purpose: a deep ref
 * would make the task reactive and auto-unwrap, which breaks the typed
 * `:pdf` binding (and pdf.js objects must not be proxied anyway).
 */
type PdfTask = NonNullable<ReturnType<typeof usePDF>['pdf']['value']>;
const pdfTask = shallowRef<PdfTask | undefined>(undefined);
const pdfPageCount = ref<number>(0);

// Inner width of the scroll host — the width a page fills at 100% zoom.
const baseWidth = ref<number>(0);

// ── Zoom ─────────────────────────────────────────────────────────────────

const ZOOM_MIN = 0.3;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.1;
const zoomLevel = ref<number>(1);
const fittedZoom = ref<number>(1);
const zoomPercent = computed(() => Math.round(zoomLevel.value * 100));

/** Explicit render width for each PDF page — re-rendered (crisp) on zoom. */
const pdfPageWidth = computed<number>(() =>
  Math.max(1, Math.round(baseWidth.value * zoomLevel.value))
);

function clampZoom(value: number): number {
  const bounded = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value));
  // Avoid floating-point drift (0.30000000000000004 → 0.3).
  return Math.round(bounded * 10) / 10;
}

function zoomIn(): void {
  zoomLevel.value = clampZoom(zoomLevel.value + ZOOM_STEP);
}

function zoomOut(): void {
  zoomLevel.value = clampZoom(zoomLevel.value - ZOOM_STEP);
}

function resetZoom(): void {
  zoomLevel.value = fittedZoom.value;
}

/** Measure the width a PDF page should fill at 100% (host minus padding + scrollbar). */
function measureBaseWidth(): void {
  const host = scrollHost.value;
  if (!host) return;
  const style = window.getComputedStyle(host);
  const padL = parseFloat(style.paddingLeft) || 0;
  const padR = parseFloat(style.paddingRight) || 0;
  baseWidth.value = Math.max(1, host.clientWidth - padL - padR);
}

function handleResize(): void {
  if (previewMode.value === 'pdf') measureBaseWidth();
}

/** Scale that makes the first rendered docx page fit the scroll host. */
function computeFitZoom(): number | null {
  const container = previewContainer.value;
  const host = scrollHost.value;
  if (!container || !host) return null;

  const firstPage = container.querySelector<HTMLElement>('section');
  if (!firstPage || firstPage.offsetWidth === 0) return null;

  // `zoom` scales both visual and layout, so offsetWidth reflects the scaled
  // width. Divide by current zoom to recover the natural page width.
  const currentZoom = zoomLevel.value || 1;
  const naturalPageWidth = firstPage.offsetWidth / currentZoom;

  const hostStyle = window.getComputedStyle(host);
  const padL = parseFloat(hostStyle.paddingLeft) || 0;
  const padR = parseFloat(hostStyle.paddingRight) || 0;
  // Reserve room for the vertical scrollbar on multi-page content.
  const availableWidth = host.clientWidth - padL - padR - 18;

  if (naturalPageWidth <= 0 || availableWidth <= 0) return null;
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, availableWidth / naturalPageWidth));
}

// ── Rendering ────────────────────────────────────────────────────────────

async function renderPdf(blob: Blob): Promise<void> {
  previewMode.value = 'pdf';
  pdfPageCount.value = 0;
  pdfTask.value = undefined;
  const data = new Uint8Array(await blob.arrayBuffer());
  // Lazy usePDF call — same pattern as GaiaVaultFilePdfPreview.
  const { pdf } = usePDF({ data });
  watch(pdf, async loadedTask => {
    if (!loadedTask) return;
    const doc = await loadedTask.promise;
    pdfPageCount.value = doc.numPages;
    pdfTask.value = loadedTask;
    await nextTick();
    // Measure after the pages mount so each renders at the right width.
    measureBaseWidth();
    rendering.value = false;
  });
}

async function renderDocx(blob: Blob): Promise<void> {
  previewMode.value = 'docx';
  await nextTick();
  const container = previewContainer.value;
  if (!container) return;
  container.innerHTML = '';
  await renderAsync(blob, container, undefined, {
    inWrapper: true,
    ignoreWidth: false,
    ignoreHeight: false,
    ignoreFonts: false,
    breakPages: true,
    experimental: false,
    trimXmlDeclaration: true,
    useBase64URL: false,
  });
  // Fit the page to the modal width once layout has settled.
  await new Promise<void>(resolve => {
    window.requestAnimationFrame(() => {
      resolve();
    });
  });
  const fit = computeFitZoom();
  if (fit !== null) {
    zoomLevel.value = fit;
    fittedZoom.value = fit;
  }
  rendering.value = false;
}

watch(
  () => [props.show, props.blob] as const,
  async ([show, blob]) => {
    if (!show || !blob) return;
    // The modal body mounts lazily — wait for the container to exist.
    await nextTick();
    rendering.value = true;
    renderError.value = null;
    zoomLevel.value = 1;
    fittedZoom.value = 1;
    try {
      if (blob.type.includes('pdf')) {
        await renderPdf(blob);
      } else {
        await renderDocx(blob);
      }
    } catch (error) {
      logger.error('Failed to render sample preview:', error);
      renderError.value = 'Failed to display the preview.';
      rendering.value = false;
    }
  }
);

window.addEventListener('resize', handleResize);
onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});

// --- chrome (the wizard preview's page-on-canvas recipe, on the shared tokens) ---
const canvas = gaiaNeutral.surfaceMuted;
const canvasBorder = gaiaNeutral.borderCard;
const page = gaiaNeutral.surface;
const pageLift = gaiaShadow.card;
const zoomTrackBorder = gaiaNeutral.borderCard;
</script>

<template>
  <gaia-modal v-model:show="showModal" width="min(720px, calc(100vw - 16px))">
    <template #header>
      <gaia-header :level="2" size="dialog">Preview with sample data</gaia-header>
    </template>

    <n-flex vertical :size="10" :wrap="false">
      <n-flex align="center" justify="space-between" :size="10" :wrap="false">
        <gaia-text size="meta" tone="subtle" :style="{ minWidth: 0 }">
          Filled with sample values — template defaults are used where declared.
        </gaia-text>
        <!-- The same zoom cluster as the wizard's live preview. -->
        <n-flex
          align="center"
          :size="2"
          :wrap="false"
          class="zoom-control"
          role="group"
          aria-label="Zoom"
        >
          <gaia-icon-button
            :dimension="26"
            quaternary
            aria-label="Zoom out"
            :disabled="rendering || zoomLevel <= ZOOM_MIN"
            @click="zoomOut"
          >
            <n-icon :size="14" :component="IconRemove" />
          </gaia-icon-button>
          <gaia-tooltip placement="bottom">
            <template #trigger>
              <gaia-button
                text
                size="tiny"
                :disabled="rendering"
                :style="{ minWidth: '40px', fontVariantNumeric: 'tabular-nums' }"
                @click="resetZoom"
              >
                {{ zoomPercent }}%
              </gaia-button>
            </template>
            Reset zoom to fit
          </gaia-tooltip>
          <gaia-icon-button
            :dimension="26"
            quaternary
            aria-label="Zoom in"
            :disabled="rendering || zoomLevel >= ZOOM_MAX"
            @click="zoomIn"
          >
            <n-icon :size="14" :component="IconAdd" />
          </gaia-icon-button>
        </n-flex>
      </n-flex>

      <gaia-banner v-if="renderError" plain type="error">{{ renderError }}</gaia-banner>
      <n-spin v-else :show="rendering">
        <div ref="scrollHost" class="sample-preview">
          <!-- PDF: render each page at an explicit width so zoom re-renders
               crisply (no CSS bitmap scaling). -->
          <div v-if="previewMode === 'pdf' && pdfTask && baseWidth > 0" class="pdf-pages">
            <div v-for="pageNumber in pdfPageCount" :key="pageNumber" class="pdf-page">
              <VuePDF :pdf="pdfTask" :page="pageNumber" :width="pdfPageWidth" />
            </div>
          </div>
          <!-- docx fallback: vector HTML text, CSS zoom stays crisp. -->
          <div
            v-show="previewMode === 'docx'"
            ref="previewContainer"
            :style="{ zoom: zoomLevel }"
          />
        </div>
      </n-spin>
    </n-flex>
  </gaia-modal>
</template>

<style scoped>
/* A bounded scroll region with the document pages laid on a tinted canvas —
   the wizard preview's recipe. Naive has no scroll-region primitive. */
.sample-preview {
  min-height: 120px;
  max-height: calc(100vh - 230px);
  overflow: auto;
  background: v-bind(canvas);
  border: 1px solid v-bind(canvasBorder);
  border-radius: 10px;
  padding: 12px;
}

.zoom-control {
  flex: none;
  padding: 0 2px;
  border: 1px solid v-bind(zoomTrackBorder);
  border-radius: 6px;
}

/* PDF pages stack vertically with a gutter, centered like a print preview. */
.pdf-pages {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.pdf-page {
  background: v-bind(page);
  box-shadow: v-bind(pageLift);
}

/* docx-preview output cleanup — same neutralisation the wizard's live
   preview applies; `!important` because the library sets inline styles.
   Pages keep their true geometry so breaks land where Word puts them. */
.sample-preview :deep(.docx-wrapper) {
  background: transparent !important;
  padding: 0 !important;
  margin: 0 !important;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.sample-preview :deep(.docx-wrapper > section.docx) {
  background: v-bind(page);
  box-shadow: v-bind(pageLift);
  margin: 0 !important;
}
</style>
